import { useState } from "react";
import { useFgp } from "@/context/FgpContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Phone, Mail, MapPin, GraduationCap, Stethoscope, Scale, Shield, Smile, Users } from "lucide-react";
import { motion } from "framer-motion";
import { AddContactDialog } from "@/components/EntityDialogs";

const catIcon = { School: GraduationCap, Doctor: Stethoscope, Lawyer: Scale, Insurance: Shield, Dentist: Smile, Teacher: GraduationCap };
const cats = ["All", "School", "Doctor", "Lawyer", "Insurance", "Dentist", "Teacher"];

export default function Contacts() {
  const { contacts, getPerson } = useFgp();
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);

  const filtered = contacts.filter((c) =>
    (filter === "All" || c.category === filter) &&
    c.name.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div>
      <PageHeader title="Contacts" subtitle="Trusted external contacts — schools, doctors, lawyers and providers, each linked to the people they serve."
        actions={<Button onClick={() => setAddOpen(true)} className="bg-neutral-600 hover:bg-neutral-700 gap-2" data-testid="add-contact"><Plus className="h-4 w-4" /> Add contact</Button>} />

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contacts…" className="pl-9 bg-white" data-testid="contacts-search" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {cats.map((c) => (
            <button key={c} onClick={() => setFilter(c)} data-testid={`contact-filter-${c}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === c ? "bg-neutral-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{c}</button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c, i) => {
          const Icon = catIcon[c.category] || Shield;
          return (
            <motion.div key={c.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid={`contact-${c.id}`}>
              <div className="flex items-start gap-3">
                {c.avatar
                  ? <img src={c.avatar} alt={c.name} className="h-10 w-10 rounded-lg object-cover flex-shrink-0" />
                  : <div className="h-10 w-10 rounded-lg bg-neutral-50 text-neutral-600 flex items-center justify-center flex-shrink-0"><Icon className="h-5 w-5" /></div>}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{c.name}</p>
                  <p className="text-xs text-slate-400">{c.category}{c.role ? ` · ${c.role}` : ""}</p>
                </div>
              </div>
              <div className="mt-4 space-y-1.5 text-sm">
                <p className="flex items-center gap-2 text-slate-600"><Phone className="h-3.5 w-3.5 text-slate-400" /> {c.phone}</p>
                <p className="flex items-center gap-2 text-slate-600 truncate"><Mail className="h-3.5 w-3.5 text-slate-400" /> {c.email}</p>
                <p className="flex items-center gap-2 text-slate-500"><MapPin className="h-3.5 w-3.5 text-slate-400" /> {c.address}</p>
              </div>
              {(c.linkedPeople?.length > 0) && (
                <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                  <Users className="h-3.5 w-3.5 text-slate-400" />
                  {c.linkedPeople.map((pid) => {
                    const p = getPerson(pid);
                    return p ? <span key={pid} className="text-xs bg-neutral-50 text-neutral-700 px-2 py-0.5 rounded-full">{p.name.split(" ")[0]}</span> : null;
                  })}
                </div>
              )}
              {c.notes && <p className="mt-3 text-xs text-slate-400 bg-slate-50 rounded-lg px-3 py-2">{c.notes}</p>}
            </motion.div>
          );
        })}
      </div>

      <AddContactDialog open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}
